import type {
  DepartmentTimeTrackingSettingsGetApiData,
  DepartmentTimeTrackingSettingsGetApiResponses,
  DepartmentTimeTrackingSettingsPatchApiData,
  DepartmentTimeTrackingSettingsPatchApiResponses,
  MyClockInForecastApiData,
  MyClockInForecastApiResponses,
  MyOvertimeEntryReasonApiData,
  MyOvertimeEntryReasonApiResponses,
  MyTimeLogClockInApiData,
  MyTimeLogClockInApiResponses,
  MyTimeLogClockOutApiData,
  MyTimeLogClockOutApiResponses,
  MyTimeLogCorrectionRequestCreateApiData,
  MyTimeLogCorrectionRequestCreateApiResponses,
  MyTimeLogCorrectionRequestListApiData,
  MyTimeLogCorrectionRequestListApiResponses,
  MyTimeLogCorrectionRequestWithdrawApiData,
  MyTimeLogCorrectionRequestWithdrawApiResponses,
  MyTimeLogListApiData,
  MyTimeLogListApiResponses,
  MyTimeLogWorkDescriptionApiData,
  MyTimeLogWorkDescriptionApiResponses,
  MyTimeTrackingStatusApiData,
  MyTimeTrackingStatusApiResponses,
  OvertimeConfigCreateApiData,
  OvertimeConfigCreateApiResponses,
  OvertimeConfigDeleteApiData,
  OvertimeConfigDeleteApiResponses,
  OvertimeConfigListApiData,
  OvertimeConfigListApiResponses,
  OvertimeConfigUpdateApiData,
  OvertimeConfigUpdateApiResponses,
  OvertimeEntriesBulkApproveApiData,
  OvertimeEntriesBulkApproveApiResponses,
  OvertimeEntriesBulkRejectApiData,
  OvertimeEntriesBulkRejectApiResponses,
  OvertimeEntryListApiData,
  OvertimeEntryListApiResponses,
  OvertimeEntryWithdrawApiData,
  OvertimeEntryWithdrawApiResponses,
  TeamStatusApiData,
  TeamStatusApiResponses,
  TeamTimelineApiData,
  TeamTimelineApiResponses,
  TimeLogClockOutApiData,
  TimeLogClockOutApiResponses,
  TimeLogCorrectApiData,
  TimeLogCorrectApiResponses,
  TimeLogCorrectionRequestApproveApiData,
  TimeLogCorrectionRequestApproveApiResponses,
  TimeLogCorrectionRequestListApiData,
  TimeLogCorrectionRequestListApiResponses,
  TimeLogCorrectionRequestRejectApiData,
  TimeLogCorrectionRequestRejectApiResponses,
  TimeLogDeleteApiData,
  TimeLogDeleteApiResponses,
  TimeLogEventListApiData,
  TimeLogEventListApiResponses,
  TimeLogEventsApiData,
  TimeLogEventsApiResponses,
  TimeLogExportApiData,
  TimeLogExportApiResponses,
  TimeLogGetApiData,
  TimeLogGetApiResponses,
  TimeLogListApiData,
  TimeLogListApiResponses,
  TimeLogRegisterApiData,
  TimeLogRegisterApiResponses,
  WorkedHoursReportApiData,
  WorkedHoursReportApiResponses,
  WorkedHoursReportExportApiData,
  WorkedHoursReportExportApiResponses,
  WorkerTimelineApiData,
  WorkerTimelineApiResponses
} from '@navio/nsdk';

import { downloadToExcel } from '../../downloadToExcel';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export const getTimeTrackingUrls = {
  // ---- Worker /me ----
  getMyClockInForecast: dataEndpoint<MyClockInForecastApiData, MyClockInForecastApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/me/clock-in-forecast/', queryParams)
  }),
  postMyClockIn: responseEndpoint<MyTimeLogClockInApiData, MyTimeLogClockInApiResponses>({
    getUrl: () => apiUrl('time-tracking/me/clock-in/'),
    getBody: (data) => data
  }),
  postMyClockOut: responseEndpoint<MyTimeLogClockOutApiData, MyTimeLogClockOutApiResponses>({
    getUrl: () => apiUrl('time-tracking/me/clock-out/'),
    getBody: (data) => data
  }),
  getMyTimeTrackingStatus: dataEndpoint<MyTimeTrackingStatusApiData, MyTimeTrackingStatusApiResponses>({
    getUrl: () => apiUrl('time-tracking/me/status/')
  }),
  getMyTimeLogs: dataEndpoint<MyTimeLogListApiData, MyTimeLogListApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/me/time-logs/', queryParams)
  }),
  patchMyOvertimeEntryReason: responseEndpoint<MyOvertimeEntryReasonApiData, MyOvertimeEntryReasonApiResponses>({
    getUrl: ({ overtimeEntryId }) => apiUrl(`time-tracking/me/overtime-entries/${overtimeEntryId}/reason/`),
    getBody: (data) => data
  }),
  patchMyTimeLogWorkDescription: responseEndpoint<
    MyTimeLogWorkDescriptionApiData,
    MyTimeLogWorkDescriptionApiResponses
  >({
    getUrl: ({ timeLogId }) => apiUrl(`time-tracking/me/time-logs/${timeLogId}/work-description/`),
    getBody: (data) => data
  }),

  // ---- Worker: correction requests (propose a fix, manager decides) ----
  postMyTimeLogCorrectionRequest: responseEndpoint<
    MyTimeLogCorrectionRequestCreateApiData,
    MyTimeLogCorrectionRequestCreateApiResponses
  >({
    getUrl: ({ timeLogId }) => apiUrl(`time-tracking/me/time-logs/${timeLogId}/correction-requests/`),
    getBody: ({ timeLogId: _timeLogId, ...body }) => body
  }),
  getMyCorrectionRequests: dataEndpoint<
    MyTimeLogCorrectionRequestListApiData,
    MyTimeLogCorrectionRequestListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('time-tracking/me/correction-requests/', queryParams)
  }),
  patchMyCorrectionRequestWithdraw: responseEndpoint<
    MyTimeLogCorrectionRequestWithdrawApiData,
    MyTimeLogCorrectionRequestWithdrawApiResponses
  >({
    getUrl: ({ correctionRequestId }) =>
      apiUrl(`time-tracking/me/correction-requests/${correctionRequestId}/withdraw/`),
    getBody: ({ correctionRequestId: _correctionRequestId, ...body }) => body
  }),

  // ---- Manager: overtime entries ----
  getOvertimeEntries: dataEndpoint<OvertimeEntryListApiData, OvertimeEntryListApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/overtime-entries/', queryParams)
  }),
  // Approve/decline take a list of ids (the single case is a one-id list), so
  // one endpoint each serves both the batch bar and the row detail sheet.
  postOvertimeEntriesBulkApprove: responseEndpoint<
    OvertimeEntriesBulkApproveApiData,
    OvertimeEntriesBulkApproveApiResponses
  >({
    getUrl: () => apiUrl('time-tracking/overtime-entries/bulk-approve/'),
    getBody: (data) => data
  }),
  postOvertimeEntriesBulkReject: responseEndpoint<
    OvertimeEntriesBulkRejectApiData,
    OvertimeEntriesBulkRejectApiResponses
  >({
    getUrl: () => apiUrl('time-tracking/overtime-entries/bulk-reject/'),
    getBody: (data) => data
  }),
  patchOvertimeEntryWithdraw: responseEndpoint<OvertimeEntryWithdrawApiData, OvertimeEntryWithdrawApiResponses>({
    getUrl: ({ overtimeEntryId }) => apiUrl(`time-tracking/overtime-entries/${overtimeEntryId}/withdraw/`),
    getBody: (data) => data
  }),

  // ---- Manager: live overtime-proximity team board ----
  getTeamStatus: dataEndpoint<TeamStatusApiData, TeamStatusApiResponses>({
    getUrl: ({ serviceDepartmentId }) => apiUrl(`time-tracking/service-departments/${serviceDepartmentId}/team-status/`)
  }),

  // ---- Manager: worked-hours compliance report ----
  getWorkedHoursReport: dataEndpoint<WorkedHoursReportApiData, WorkedHoursReportApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/worked-hours-report/', queryParams)
  }),
  getWorkedHoursReportExport: dataEndpoint<WorkedHoursReportExportApiData, WorkedHoursReportExportApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/worked-hours-report/export/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),

  // ---- Manager: per-worker week timeline ----
  getTeamTimeline: dataEndpoint<TeamTimelineApiData, TeamTimelineApiResponses>({
    getUrl: ({ serviceDepartmentId, ...query }) =>
      apiUrl(`time-tracking/service-departments/${serviceDepartmentId}/timeline/`, query)
  }),
  getWorkerTimeline: dataEndpoint<WorkerTimelineApiData, WorkerTimelineApiResponses>({
    getUrl: ({ serviceWorkerContractId, ...queryParams }) =>
      apiUrl(`time-tracking/service-worker-contracts/${serviceWorkerContractId}/timeline/`, queryParams)
  }),

  // ---- Manager: time logs ----
  getTimeLogs: dataEndpoint<TimeLogListApiData, TimeLogListApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/time-logs/', queryParams)
  }),
  // Hours the worker never clocked, registered after the fact — a closed log
  // from the start. Overtime derives like any other log and lands pending.
  postTimeLogRegistration: responseEndpoint<TimeLogRegisterApiData, TimeLogRegisterApiResponses>({
    getUrl: () => apiUrl('time-tracking/time-logs/'),
    getBody: (data) => data
  }),
  // Same department scope and browse lenses as getTimeLogs, unpaginated — the
  // file is what the table is showing, not a second definition of it.
  getTimeLogsExport: dataEndpoint<TimeLogExportApiData, TimeLogExportApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/time-logs/export/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  getTimeLog: dataEndpoint<TimeLogGetApiData, TimeLogGetApiResponses>({
    getUrl: ({ timeLogId }) => apiUrl(`time-tracking/time-logs/${timeLogId}/`)
  }),
  getTimeLogEvents: dataEndpoint<TimeLogEventsApiData, TimeLogEventsApiResponses>({
    getUrl: ({ timeLogId }) => apiUrl(`time-tracking/time-logs/${timeLogId}/events/`)
  }),
  // Every log's events across the department, newest first — the activity
  // feed. Events on deleted logs stay listed.
  getTimeLogEventList: dataEndpoint<TimeLogEventListApiData, TimeLogEventListApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/time-log-events/', queryParams)
  }),
  // Stops a worker's running timer on their behalf (a forgotten clock-out) —
  // now, or backdated via clockOutAt. Overtime derives like their own close.
  postTimeLogClockOut: responseEndpoint<TimeLogClockOutApiData, TimeLogClockOutApiResponses>({
    getUrl: ({ timeLogId }) => apiUrl(`time-tracking/time-logs/${timeLogId}/clock-out/`),
    getBody: ({ timeLogId: _timeLogId, ...body }) => body
  }),
  patchTimeLogCorrect: responseEndpoint<TimeLogCorrectApiData, TimeLogCorrectApiResponses>({
    getUrl: ({ timeLogId }) => apiUrl(`time-tracking/time-logs/${timeLogId}/correct/`),
    getBody: (data) => data
  }),
  deleteTimeLog: dataEndpoint<TimeLogDeleteApiData, TimeLogDeleteApiResponses>({
    getUrl: ({ timeLogId }) => apiUrl(`time-tracking/time-logs/${timeLogId}/`)
  }),

  // ---- Manager: correction requests ----
  // Approve applies the proposal through the same correction service the manager's
  // own edit dialog uses, so it refuses on the same grounds (paid hours, frozen
  // run, overlap, a decided sibling's overtime).
  getCorrectionRequests: dataEndpoint<TimeLogCorrectionRequestListApiData, TimeLogCorrectionRequestListApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/correction-requests/', queryParams)
  }),
  patchCorrectionRequestApprove: responseEndpoint<
    TimeLogCorrectionRequestApproveApiData,
    TimeLogCorrectionRequestApproveApiResponses
  >({
    getUrl: ({ correctionRequestId }) => apiUrl(`time-tracking/correction-requests/${correctionRequestId}/approve/`),
    getBody: ({ correctionRequestId: _correctionRequestId, ...body }) => body
  }),
  patchCorrectionRequestReject: responseEndpoint<
    TimeLogCorrectionRequestRejectApiData,
    TimeLogCorrectionRequestRejectApiResponses
  >({
    getUrl: ({ correctionRequestId }) => apiUrl(`time-tracking/correction-requests/${correctionRequestId}/reject/`),
    getBody: ({ correctionRequestId: _correctionRequestId, ...body }) => body
  }),

  // ---- Staff/org-admin: overtime configs ----
  getOvertimeConfigs: dataEndpoint<OvertimeConfigListApiData, OvertimeConfigListApiResponses>({
    getUrl: (queryParams) => apiUrl('time-tracking/overtime-configs/', queryParams)
  }),
  postOvertimeConfig: responseEndpoint<OvertimeConfigCreateApiData, OvertimeConfigCreateApiResponses>({
    getUrl: () => apiUrl('time-tracking/overtime-configs/'),
    getBody: (data) => data
  }),
  patchOvertimeConfig: responseEndpoint<OvertimeConfigUpdateApiData, OvertimeConfigUpdateApiResponses>({
    getUrl: ({ overtimeConfigId }) => apiUrl(`time-tracking/overtime-configs/${overtimeConfigId}/`),
    getBody: (data) => data
  }),
  deleteOvertimeConfig: dataEndpoint<OvertimeConfigDeleteApiData, OvertimeConfigDeleteApiResponses>({
    getUrl: ({ overtimeConfigId }) => apiUrl(`time-tracking/overtime-configs/${overtimeConfigId}/`)
  }),

  // ---- Manager: per-department settings ----
  getDepartmentTimeTrackingSettings: dataEndpoint<
    DepartmentTimeTrackingSettingsGetApiData,
    DepartmentTimeTrackingSettingsGetApiResponses
  >({
    getUrl: ({ serviceDepartmentId }) => apiUrl(`time-tracking/service-departments/${serviceDepartmentId}/settings/`)
  }),
  patchDepartmentTimeTrackingSettings: responseEndpoint<
    DepartmentTimeTrackingSettingsPatchApiData,
    DepartmentTimeTrackingSettingsPatchApiResponses
  >({
    getUrl: ({ serviceDepartmentId }) => apiUrl(`time-tracking/service-departments/${serviceDepartmentId}/settings/`),
    getBody: (data) => data
  })
};
